import type {
  AgentExecutionDetail,
  AgentExecutionListItem,
  AgentExecutionLogEntry,
  AgentExecutionOutput,
  AgentExecutionStatus,
  PipelineLogsAgentItem
} from './types';

import type { ExecutionLog } from '~/api/testV2';
import {
  AGENT_LOG_EVENT,
  bodyIncludesAgentLogEvent,
  extractAgentOutputMessagePayload
} from '~/entities/agentLogMarkers';

export type AgentExecutionMetadata = Pick<
  AgentExecutionListItem,
  | 'pipelineKey'
  | 'pipelineName'
  | 'pipelineVersion'
  | 'connectorId'
  | 'agentName'
>;

type ExecutionMetadata = AgentExecutionMetadata;

function getAttribute(
  item: PipelineLogsAgentItem,
  key: keyof PipelineLogsAgentItem['attributes']
): string {
  return item.attributes?.[key] ?? '';
}

function readExecutionMetadata(item: PipelineLogsAgentItem): ExecutionMetadata {
  return {
    pipelineKey: getAttribute(item, 'pipeline.key'),
    pipelineName: getAttribute(item, 'pipeline.name'),
    pipelineVersion: getAttribute(item, 'pipeline.version'),
    connectorId: getAttribute(item, 'connector.id'),
    agentName: getAttribute(item, 'step.name')
  };
}

function sortLogsByTimestamp(
  logs: PipelineLogsAgentItem[],
  order: 'ascending' | 'descending'
): PipelineLogsAgentItem[] {
  return [...logs].sort((a, b) => {
    const diff =
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    return order === 'ascending' ? diff : -diff;
  });
}

export function toAgentExecutionLogEntry(
  item: PipelineLogsAgentItem,
  index: number
): AgentExecutionLogEntry {
  const logId =
    item.attributes?.['log.id'] ?? `${item.timestamp}-${item.spanId ?? index}`;

  return {
    id: logId,
    timestamp: item.timestamp,
    severityText: item.severityText,
    body: item.body ?? '',
    attributes: item.attributes ?? {}
  };
}

export function deriveAgentExecutionStatus(
  logs: AgentExecutionLogEntry[]
): AgentExecutionStatus {
  if (
    logs.some(log => bodyIncludesAgentLogEvent(log.body, AGENT_LOG_EVENT.ERROR))
  ) {
    return 'FAILED';
  }

  if (
    logs.some(log =>
      bodyIncludesAgentLogEvent(log.body, AGENT_LOG_EVENT.OUTPUT_MESSAGE)
    )
  ) {
    return 'SUCCESS';
  }

  if (
    logs.some(log => bodyIncludesAgentLogEvent(log.body, AGENT_LOG_EVENT.INIT))
  ) {
    return 'RUNNING';
  }

  return 'UNKNOWN';
}

export function extractAgentOutput(
  logs: AgentExecutionLogEntry[]
): AgentExecutionOutput {
  const outputLog = [...logs]
    .reverse()
    .find(log =>
      bodyIncludesAgentLogEvent(log.body, AGENT_LOG_EVENT.OUTPUT_MESSAGE)
    );

  if (!outputLog) {
    return { body: null, structured: null };
  }

  const raw = extractAgentOutputMessagePayload(outputLog.body);

  let structured: Record<string, unknown> | null = null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      structured = parsed as Record<string, unknown>;
    }
  } catch {
    structured = null;
  }

  return {
    body: raw || null,
    structured
  };
}

export function toAgentExecutionListItems(
  logs: PipelineLogsAgentItem[]
): AgentExecutionListItem[] {
  return logs.map((log, index) => {
    const entry = toAgentExecutionLogEntry(log, index);

    return {
      id: entry.id,
      agentExecutionKey: getAttribute(log, 'agent.execution.key'),
      timestamp: log.timestamp,
      contentPreview: log.body ?? '',
      ...readExecutionMetadata(log)
    };
  });
}

function isAgentExecutionLogMessage(logMessage: string): boolean {
  return logMessage.trim().toLowerCase().startsWith('agent.');
}

export function buildAgentExecutionDetailFromExecutionLogs(
  agentExecutionKey: string,
  metadata: AgentExecutionMetadata,
  logs: ExecutionLog[]
): AgentExecutionDetail | null {
  const agentLogs = logs
    .filter(
      log =>
        typeof log.logMessage === 'string' &&
        isAgentExecutionLogMessage(log.logMessage)
    )
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  if (agentLogs.length === 0) {
    return null;
  }

  const logEntries: AgentExecutionLogEntry[] = agentLogs.map((log, index) => ({
    id: log.id || `${log.timestamp}-${index}`,
    timestamp: log.timestamp,
    severityText: log.logLevel,
    body: log.logMessage,
    attributes: {}
  }));
  const status = deriveAgentExecutionStatus(logEntries);

  return {
    agentExecutionKey,
    status,
    startedAt: logEntries[0].timestamp,
    finishedAt:
      status === 'RUNNING' ? null : logEntries[logEntries.length - 1].timestamp,
    output: extractAgentOutput(logEntries),
    logs: logEntries,
    canCollect: status === 'SUCCESS' || status === 'FAILED',
    ...metadata
  };
}

export function buildAgentExecutionDetail(
  agentExecutionKey: string,
  logs: PipelineLogsAgentItem[]
): AgentExecutionDetail {
  const sorted = sortLogsByTimestamp(logs, 'ascending');
  const logEntries = sorted.map(toAgentExecutionLogEntry);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const status = deriveAgentExecutionStatus(logEntries);
  const output = extractAgentOutput(logEntries);

  return {
    agentExecutionKey,
    status,
    startedAt: first.timestamp,
    finishedAt: status === 'RUNNING' ? null : last.timestamp,
    output,
    logs: logEntries,
    canCollect: status === 'SUCCESS' || status === 'FAILED',
    ...readExecutionMetadata(first)
  };
}
